import { Badge } from '@artic/ui/badge';
import { DraftStateIcon, LiveStateIcon, StagedStateIcon } from '@artic/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useOperationStagedRuleCount } from '../change-control/staged-changes';
import { usePartnerId } from '../inbox/use-partner-name';
import { operationRulesQueryOptions, operationRunSheetsQueryOptions } from './queries';

export type OperationLifecycleState = 'draft' | 'staged' | 'live';

/**
 * ENG-813 Card 1. Distinct from `operation.draft_saved_at` (the ENG-764
 * autosave draft for the Checks/Stations/Details forms, surfaced by
 * `OperationReleaseAffordance`) — "Draft" here means "has no Rules yet".
 */
export function deriveOperationLifecycleState(args: {
  ruleCount: number;
  behindTotal: number;
  stagedCount: number;
  usageCount: number;
}): OperationLifecycleState {
  const { ruleCount, behindTotal, stagedCount, usageCount } = args;
  if (ruleCount === 0) return 'draft';
  if (behindTotal > 0 || stagedCount > 0 || usageCount === 0) return 'staged';
  return 'live';
}

const STATE_CONFIG: Record<
  OperationLifecycleState,
  { icon: typeof DraftStateIcon; className: string }
> = {
  draft: { icon: DraftStateIcon, className: '' },
  staged: {
    icon: StagedStateIcon,
    className: 'border-status-warning/30 bg-status-warning/10 text-status-warning',
  },
  live: {
    icon: LiveStateIcon,
    className: 'border-status-good/30 bg-status-good/10 text-status-good-text',
  },
};

interface OperationLifecycleBadgeProps {
  operationId: string;
}

/**
 * Draft / Staged / Live badge rendered inline with the breadcrumb via the
 * route's `breadcrumbBadges` resolver, on all four operation-detail tabs.
 * Fetches its own data (rules count, Run Sheet usage, session-staged rule
 * count) rather than reading `OperationReleaseAffordance`'s queries, so it
 * renders correctly regardless of which tab mounted first.
 */
export function OperationLifecycleBadge({ operationId }: OperationLifecycleBadgeProps) {
  const { t } = useTranslation('common');
  const partnerId = usePartnerId();
  const { data: rules } = useQuery(operationRulesQueryOptions(operationId));
  const { data: usage } = useQuery(operationRunSheetsQueryOptions(operationId));
  const stagedCount = useOperationStagedRuleCount(partnerId, operationId);

  if (!rules || !usage) return null;

  const behindTotal = usage.reduce((sum, row) => sum + row.work_orders_behind_count, 0);
  const state = deriveOperationLifecycleState({
    ruleCount: rules.length,
    behindTotal,
    stagedCount,
    usageCount: usage.length,
  });
  const { icon: Icon, className } = STATE_CONFIG[state];
  const label = t(`terms.${state}` as const);

  return (
    <Badge
      variant="outline"
      data-slot="operation-lifecycle-badge"
      data-state={state}
      className={className}
    >
      <Icon className="size-3" aria-hidden />
      {label}
    </Badge>
  );
}
